/**
 * 关系系统 Store
 * 实现动态好感度追踪、关系类型自动判定、历史事件记录
 */

import type { Relationship, RelationshipAdjustment, RelationshipEvent, RelationshipStats, RelationshipType } from '../types/relationship'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'

import { useOnboardingStore } from './onboarding'

export const useRelationshipStore = defineStore('relationship', () => {
  // ============ 状态管理 ============

  /** 关系映射表 (characterId -> Relationship) */
  const relationships = useLocalStorage<Map<string, Relationship>>(
    'relationship/data',
    new Map(),
    {
      serializer: {
        read: (v: string) => new Map(JSON.parse(v)),
        write: (v: Map<string, Relationship>) => JSON.stringify([...v]),
      },
    },
  )

  // ============ 计算属性 ============

  /** 所有关系列表 */
  const allRelationships = computed(() => Array.from(relationships.value.values()))

  /** 按好感度排序的关系 */
  const sortedByAffection = computed(() =>
    allRelationships.value.sort((a, b) => b.affection - a.affection),
  )

  /** 最亲密的关系 */
  const closestRelationship = computed(() => sortedByAffection.value[0])

  /** 正面关系（好感度 > 0） */
  const positiveRelationships = computed(() =>
    allRelationships.value.filter(r => r.affection > 0),
  )

  /** 负面关系（好感度 < 0） */
  const negativeRelationships = computed(() =>
    allRelationships.value.filter(r => r.affection < 0),
  )

  // ============ 核心功能：获取关系 ============

  /**
   * 获取指定角色的关系
   */
  function getRelationship(characterId: string): Relationship | undefined {
    return relationships.value.get(characterId)
  }

  /**
   * 获取或创建关系（如果不存在则创建新关系）
   */
  function getOrCreateRelationship(
    characterId: string,
    characterName: string,
  ): Relationship {
    let relationship = relationships.value.get(characterId)

    if (!relationship) {
      const onboardingStore = useOnboardingStore()
      relationship = {
        characterId,
        characterName,
        affection: onboardingStore.defaultInitialAffection ?? 40,
        type: onboardingStore.defaultInitialRelationType ?? 'friend',
        establishedAt: Date.now(),
        lastInteractionAt: Date.now(),
        interactionCount: 0,
        history: [],
        tags: [],
      }
      relationships.value.set(characterId, relationship)
      console.info('✅ 创建新关系:', characterName)
    }

    return relationship
  }

  // ============ 核心功能：更新好感度 ============

  /**
   * 更新好感度
   * @param characterId 角色ID
   * @param characterName 角色名称
   * @param change 好感度变化值 (-10 到 +10)
   * @param reason 变化原因
   * @param messageContent 相关的消息内容（可选）
   */
  function updateAffection(
    characterId: string,
    characterName: string,
    change: number,
    reason: string,
    messageContent?: string,
  ) {
    const relationship = getOrCreateRelationship(characterId, characterName)

    // 更新好感度（限制在 -100 到 +100 范围内）
    relationship.affection = Math.max(-100, Math.min(100, relationship.affection + change))

    // 💡 不再自动判定关系类型，关系类型完全由用户手动控制
    // relationship.type = determineRelationshipType(relationship.affection)

    // 更新互动信息
    relationship.lastInteractionAt = Date.now()
    relationship.interactionCount++

    // 记录历史事件
    const event: RelationshipEvent = {
      id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      scoreChange: change,
      reason,
      messageContent,
    }
    relationship.history.push(event)

    // 只保留最近100条历史
    if (relationship.history.length > 100) {
      relationship.history = relationship.history.slice(-100)
    }

    console.info(
      `✅ 好感度更新: ${characterName} ${change > 0 ? '+' : ''}${change} -> ${relationship.affection} (${reason})`,
    )

    // 更新存储
    relationships.value.set(characterId, relationship)
  }

  /**
   * 处理来自 AI 的关系调整
   */
  function processRelationshipAdjustment(
    adjustment: RelationshipAdjustment,
    defaultCharacterId: string = 'user',
    defaultCharacterName: string = '用户',
  ) {
    if (!adjustment || adjustment.score_change === 0) {
      return
    }

    // 确定目标角色
    const characterId = adjustment.target_char_name === 'User'
      ? defaultCharacterId
      : adjustment.target_char_name.toLowerCase()

    const characterName = adjustment.target_char_name === 'User'
      ? defaultCharacterName
      : adjustment.target_char_name

    updateAffection(
      characterId,
      characterName,
      adjustment.score_change,
      adjustment.reason,
    )
  }

  // ============ 辅助功能 ============

  /**
   * 添加标签
   */
  function addTag(characterId: string, tag: string) {
    const relationship = relationships.value.get(characterId)
    if (relationship && !relationship.tags.includes(tag)) {
      relationship.tags.push(tag)
      relationships.value.set(characterId, relationship)
    }
  }

  /**
   * 移除标签
   */
  function removeTag(characterId: string, tag: string) {
    const relationship = relationships.value.get(characterId)
    if (relationship) {
      relationship.tags = relationship.tags.filter(t => t !== tag)
      relationships.value.set(characterId, relationship)
    }
  }

  /**
   * 获取关系统计
   */
  function getRelationshipStats(characterId: string): RelationshipStats | null {
    const relationship = relationships.value.get(characterId)
    if (!relationship)
      return null

    const positiveEvents = relationship.history.filter(e => e.scoreChange > 0).length
    const negativeEvents = relationship.history.filter(e => e.scoreChange < 0).length

    const affectionHistory = relationship.history.map(e =>
      e.scoreChange,
    ).reduce((acc, change) => {
      const last = acc[acc.length - 1] || 0
      acc.push(last + change)
      return acc
    }, [] as number[])

    return {
      totalInteractions: relationship.interactionCount,
      averageAffection: relationship.affection,
      peakAffection: Math.max(...affectionHistory, relationship.affection),
      lowestAffection: Math.min(...affectionHistory, relationship.affection),
      positiveEvents,
      negativeEvents,
    }
  }

  /**
   * 获取关系描述文本（用于注入提示词）
   */
  function getRelationshipDescription(
    characterId: string,
    options: { compact?: boolean, maxReasonLength?: number } = {},
  ): string {
    const typeDescriptions: Record<string, string> = {
      stranger: '陌生人',
      acquaintance: '认识的人',
      friend: '朋友',
      close_friend: '好友',
      best_friend: '挚友',
      lover: '恋人',
      family: '家人',
      rival: '对手',
    }

    const relationship = relationships.value.get(characterId)
    if (!relationship) {
      const onboardingStore = useOnboardingStore()
      const defaultAffection = onboardingStore.defaultInitialAffection ?? 40
      const defaultType = onboardingStore.defaultInitialRelationType ?? 'friend'
      const typeLabel = typeDescriptions[defaultType] || defaultType
      if (options.compact)
        return `关系：${typeLabel}；好感度：${defaultAffection}/100；最近变化：无`
      return `
你与对方刚刚认识：
- 关系类型: ${typeLabel}
- 好感度: ${defaultAffection}/100
- 互动次数: 0
- 最近变化: 无
      `.trim()
    }

    const typeLabel = typeDescriptions[relationship.type] || relationship.type

    const recentEvents = relationship.history
      .slice(options.compact ? -1 : -3)
      .map((e) => {
        const reason = options.maxReasonLength && e.reason.length > options.maxReasonLength
          ? `${e.reason.slice(0, options.maxReasonLength)}…`
          : e.reason
        return `${e.scoreChange > 0 ? '+' : ''}${e.scoreChange} (${reason})`
      })
      .join(', ')

    if (options.compact) {
      const tags = relationship.tags.length > 0 ? `；标签：${relationship.tags.join('、')}` : ''
      return `关系：${typeLabel}；好感度：${relationship.affection}/100；最近变化：${recentEvents || '无'}${tags}`
    }

    return `
你与 ${relationship.characterName} 的关系：
- 关系类型: ${typeLabel}
- 好感度: ${relationship.affection}/100
- 互动次数: ${relationship.interactionCount}
- 最近变化: ${recentEvents || '无'}
${relationship.tags.length > 0 ? `- 特殊标签: ${relationship.tags.join(', ')}` : ''}
    `.trim()
  }

  /**
   * 格式化关系类型为中文
   */
  function formatRelationshipType(type: RelationshipType): string {
    const typeNames: Record<string, string> = {
      stranger: '陌生人',
      acquaintance: '认识',
      friend: '朋友',
      close_friend: '好友',
      best_friend: '挚友',
      lover: '恋人',
      family: '家人',
      rival: '对手',
    }
    return typeNames[type] || type
  }

  /**
   * 手动设置关系（用于联系人设置）
   * 💡 允许用户自由设置好感度和关系类型，不强制一致性
   */
  function setRelationship(
    characterId: string,
    characterName: string,
    type: RelationshipType,
    affection: number,
    reason: string = '手动调整关系设置',
  ) {
    const relationship = getOrCreateRelationship(characterId, characterName)
    const oldAffection = relationship.affection
    const oldType = relationship.type

    // 更新好感度和关系类型（直接使用用户设置的值）
    relationship.affection = Math.max(-100, Math.min(100, affection))
    relationship.type = type // 使用用户选择的关系类型，不强制自动判定
    // 记录关系类型变更时间（用于冷却检查）
    if (oldType !== type) {
      relationship.lastTypeChangeAt = Date.now()
    }

    relationship.lastInteractionAt = Date.now()

    // 记录历史事件（手动调整）
    const event: RelationshipEvent = {
      id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      scoreChange: relationship.affection - oldAffection,
      reason,
    }
    relationship.history.push(event)

    // 只保留最近100条历史
    if (relationship.history.length > 100) {
      relationship.history = relationship.history.slice(-100)
    }

    relationships.value.set(characterId, relationship)

    console.info(
      `✅ 手动设置关系: ${characterName} (好感度: ${affection}, 关系类型: ${type})`,
    )
  }

  /**
   * 删除关系
   */
  function deleteRelationship(characterId: string) {
    if (relationships.value.has(characterId)) {
      relationships.value.delete(characterId)
      console.info(`✅ 删除关系: ${characterId}`)
    }
  }

  /**
   * 清理某个联系人的所有相关引用（关系孤儿数据）
   */
  function purgeByContactId(contactId: string) {
    deleteRelationship(contactId)
  }

  /**
   * 清空所有关系（慎用）
   */
  function clearAllRelationships() {
    relationships.value.clear()
    console.warn('⚠️ 已清空所有关系')
  }

  /**
   * 导出关系数据
   */
  function exportRelationships(): string {
    return JSON.stringify([...relationships.value], null, 2)
  }

  /**
   * 导入关系数据
   */
  function importRelationships(data: string) {
    try {
      const parsed = JSON.parse(data)
      relationships.value = new Map(parsed)
      console.info('✅ 导入关系数据成功')
    }
    catch (error) {
      console.error('❌ 导入关系数据失败:', error)
    }
  }

  // ============ 导出 ============

  return {
    // 状态
    relationships,
    allRelationships,
    sortedByAffection,
    closestRelationship,
    positiveRelationships,
    negativeRelationships,

    // 方法
    getRelationship,
    getOrCreateRelationship,
    updateAffection,
    processRelationshipAdjustment,
    setRelationship,
    addTag,
    removeTag,
    getRelationshipStats,
    getRelationshipDescription,
    formatRelationshipType,
    deleteRelationship,
    purgeByContactId,
    clearAllRelationships,
    exportRelationships,
    importRelationships,
  }
})
